import { createClient } from '@supabase/supabase-js'
import { Database } from '@/types/supabase'
import { markSessionVerified, clearSessionVerified } from '@/utils/session-utils'

// Declare the auth token retrieval flag on window
declare global {
  interface Window {
    authTokenRetrieved?: boolean
  }
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl) {
  throw new Error('Supabase URL must be set in environment variables')
}

if (!supabaseAnonKey || supabaseAnonKey === 'your-anon-key-here') {
  console.error('Invalid Supabase Anon Key. Update your .env file with a valid key.')
  throw new Error('Supabase Anon Key must be set to a valid value in environment variables')
}

// Create the Supabase client with enhanced session persistence configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'boardgames-today-auth-token', // Use a custom storage key
    storage: {
      getItem: key => {
        try {
          const itemStr = localStorage.getItem(key)
          window.authTokenRetrieved = true
          return itemStr
        } catch (error) {
          console.error('Error retrieving from localStorage:', error)
          return null
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value)
          if (value) {
            // Mark session as verified when token is saved
            markSessionVerified()
          }
          return
        } catch (error) {
          console.error('Error saving to localStorage:', error)
        }
      },
      removeItem: key => {
        try {
          localStorage.removeItem(key)
          // Clear verification flag when token is removed
          clearSessionVerified()
          return
        } catch (error) {
          console.error('Error removing from localStorage:', error)
        }
      },
    },
  },
})

// Check session state
export const checkSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession()
    if (error) {
      console.error('Error getting session:', error)
      return false
    }
    return !!data.session
  } catch (e) {
    console.error('Error checking session:', e)
    return false
  }
}

// Export a proper sign out function that cleans up local storage
export const signOutAndClearSession = async () => {
  try {
    const { error } = await supabase.auth.signOut({ scope: 'local' })
    if (error) {
      console.error('Error signing out:', error)
      return false
    }
    return true
  } catch (e) {
    console.error('Failed to sign out:', e)
    return false
  }
}

// Add a visibility change listener to handle tab switching issues
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    // Only do this when the page becomes visible again
    if (document.visibilityState === 'visible') {
      // Force a session refresh when tab becomes visible again
      supabase.auth.getSession().then(({ data, error }) => {
        if (error) {
          console.error('Error refreshing session on visibility change:', error)
          clearSessionVerified()
        } else if (data.session) {
          markSessionVerified()
          // Manually trigger an auth state change to update the UI
          const event = new CustomEvent('supabase.auth.refresh', {
            detail: { session: data.session },
          })
          window.dispatchEvent(event)
        } else {
          clearSessionVerified()
        }
      })
    }
  })
}
